import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { notify } from "../../../components/Toast";
import {
  getUserOnboardingAnswers,
  updateOnboardingAnswers,
  getOnboardingFlow,
} from "../../../services/clientWebsite";
import "./UpdateOnboardingSection.css";

// Component to update user onboarding answers
const UpdateOnboardingSection = () => {
  // State to store user answers
  const [userAnswers, setUserAnswers] = useState(null);
  // Get onboarding completion status from Redux store
  const onboardingCompleted = useSelector(
    (state) => state.auth.onboardingCompleted
  );
  // State to store onboarding sections
  const [sections, setSections] = useState([]);
  // State to store current answers
  const [answers, setAnswers] = useState({});
  // State for loading indicator
  const [loading, setLoading] = useState(false);
  // State for saving indicator
  const [saving, setSaving] = useState(false);
  // State to manage expanded sections
  const [expandedSections, setExpandedSections] = useState({});
  // Get user data from Redux store
  const user = useSelector((state) => state.auth.user);

  // Transform API response to match UI expectations
  const transformAPIResponse = (apiSections) => {
    return apiSections.map((section) => ({
      id: section.id,
      title: section.section_title,
      subtitle: section.subtitle || "",
      questions:
        section.questions?.map((question) => ({
          id: question.id,
          title: question.question_title,
          subtitle: question.question_description || "",
          type: mapQuestionType(question.question_type),
          options: extractOptionsFromAnswerFields(question.answer_fields),
          required: question.required !== false,
          maxSelect:
            question.max_select || question.answer_fields?.maxSelect || null,
          minSelect:
            question.min_select || question.answer_fields?.minSelect || null,
          placeholder: question.answer_fields?.placeholder || "",
          hasConditionalQuestions:
            question.conditional_questions &&
            Object.keys(question.conditional_questions).length > 0,
          conditionalQuestions: question.conditional_questions || {},
          gridClass: question.answer_fields?.gridClass || "",
          specialLogic: question.answer_fields?.specialLogic || null,
        })) || [],
    }));
  };

  // Extract options from answer_fields
  const extractOptionsFromAnswerFields = (answerFields) => {
    if (!answerFields) return [];
    if (Array.isArray(answerFields)) {
      return answerFields.map((field) => field.value);
    }
    if (answerFields.options && Array.isArray(answerFields.options)) {
      return answerFields.options;
    }
    return [];
  };

  // Map API question types to UI expected types
  const mapQuestionType = (apiType) => {
    const typeMap = {
      single_choice: "radio",
      multiple_choice: "checkbox",
      text: "textarea",
      email: "email",
      date: "date",
      message: "message",
      cta: "cta",
      button: "cta",
    };
    return typeMap[apiType] || "textarea";
  };

  // Fetch onboarding flow and user answers
  const fetchOnboardingData = async () => {
    setLoading(true);
    try {
      // Fetch the onboarding flow
      const flowResponse = await getOnboardingFlow();

      if (!flowResponse || !flowResponse.data) {
        throw new Error("No onboarding flow data received");
      }

      // Transform flow data to match UI structure
      const apiSections = Array.isArray(flowResponse.data)
        ? flowResponse.data
        : [];
      const transformedSections = transformAPIResponse(apiSections);
      setSections(transformedSections);

      // Fetch user's previous answers
      const answersResponse = await getUserOnboardingAnswers(user.id);

      if (answersResponse) {
        setUserAnswers(answersResponse);

        // Format answers for UI
        const formattedAnswers = {};
        answersResponse.forEach((section) => {
          if (section.questions && Array.isArray(section.questions)) {
            section.questions.forEach((q) => {
              // Handle main question answer
              if (q.user_answer !== undefined) {
                formattedAnswers[q.id] = q.user_answer;
              }

              // Handle conditional questions
              if (q.conditional_questions) {
                Object.values(q.conditional_questions).forEach(
                  (condQuestions) => {
                    if (Array.isArray(condQuestions)) {
                      condQuestions.forEach((condQ) => {
                        if (condQ.user_answer !== undefined) {
                          formattedAnswers[condQ.id] = condQ.user_answer;
                        }
                      });
                    }
                  }
                );
              }
            });
          }
        });
        setAnswers(formattedAnswers);

        // Expand all sections by default if answers exist
        const expanded = {};
        transformedSections.forEach((section) => {
          expanded[section.id] = true;
        });
        setExpandedSections(expanded);
      } else {
        // No answers found, expand first section by default
        if (transformedSections.length > 0) {
          setExpandedSections({ [transformedSections[0].id]: true });
        }
      }
    } catch (error) {
      console.error("Error fetching onboarding data:", error);
      if (error.response?.status === 404) {
        notify.error(
          "No onboarding data found. Please complete onboarding first."
        );
      } else {
        notify.error("Failed to load onboarding data");
      }
    } finally {
      setLoading(false);
    }
  };

  // Fetch data when user is available and onboarding is completed
  useEffect(() => {
    if (user?.id && onboardingCompleted) {
      fetchOnboardingData();
    }
  }, [user]);

  // Handle answer changes for questions
  const handleAnswerChange = (questionId, value) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: value,
    }));
  };

  // Handle radio button selection with deselect option
  const handleRadioToggle = (questionId, value) => {
    if (answers[questionId] === value) {
      handleAnswerChange(questionId, null);
    } else {
      handleAnswerChange(questionId, value);
    }
  };

  // Handle checkbox selection with max/min constraints
  const handleCheckboxToggle = (
    questionId,
    value,
    maxSelect = null,
    minSelect = null
  ) => {
    const currentValues = answers[questionId] || [];
    let newValues;

    // Handle deselection
    if (currentValues.includes(value)) {
      if (minSelect && currentValues.length <= minSelect) {
        notify.error(`Please select at least ${minSelect} option(s)`);
        return;
      }
      newValues = currentValues.filter((v) => v !== value);
    } else {
      // Handle selection
      if (maxSelect && currentValues.length >= maxSelect) {
        notify.error(`You can select maximum ${maxSelect} option(s)`);
        return;
      }
      newValues = [...currentValues, value];
    }
    handleAnswerChange(questionId, newValues);
  };

  // Handle special checkbox logic (e.g., "No restrictions" clears other selections)
  const handleSpecialCheckboxLogic = (questionId, value, specialLogic) => {
    const currentValues = answers[questionId] || [];
    if (specialLogic === "noRestrictions") {
      if (value === "No restrictions") {
        handleAnswerChange(questionId, [value]);
      } else {
        const filtered = currentValues.filter(
          (item) => item !== "No restrictions"
        );
        if (filtered.includes(value)) {
          handleAnswerChange(
            questionId,
            filtered.filter((item) => item !== value)
          );
        } else {
          handleAnswerChange(questionId, [...filtered, value]);
        }
      }
    } else {
      handleCheckboxToggle(questionId, value);
    }
  };

  // Toggle section visibility
  const toggleSection = (sectionId) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  };

  // Prepare payload for updating answers
  const prepareUpdatePayload = () => {
    const payload = {
      user_id: user.id,
      sections: [],
    };

    // Process conditional questions for payload
    const processConditionalQuestions = (question, answerValue) => {
      const additionalAnswers = [];

      if (question.conditionalQuestions && typeof answerValue === "string") {
        // Handle radio button conditional questions
        const selectedIndex = question.options.findIndex(
          (opt) => opt === answerValue
        );
        const conditionalKey = `${question.id}-${selectedIndex}`;

        if (question.conditionalQuestions[conditionalKey]) {
          const conditionalQs = question.conditionalQuestions[conditionalKey];
          conditionalQs.forEach((condQ) => {
            const condAnswerValue = answers[condQ.id];
            if (
              condAnswerValue !== undefined &&
              condAnswerValue !== null &&
              condAnswerValue !== ""
            ) {
              let formattedCondAnswer = condAnswerValue;
              if (
                condQ.type === "checkbox" &&
                !Array.isArray(condAnswerValue)
              ) {
                formattedCondAnswer = [condAnswerValue];
              }
              additionalAnswers.push({
                question_id: condQ.id,
                question_title: condQ.question_title || condQ.title,
                answer: formattedCondAnswer,
              });
            }
          });
        }
      } else if (question.conditionalQuestions && Array.isArray(answerValue)) {
        // Handle checkbox conditional questions
        answerValue.forEach((selected, idx) => {
          const conditionalKey = `${question.id}-${idx}`;
          if (question.conditionalQuestions[conditionalKey]) {
            const conditionalQs = question.conditionalQuestions[conditionalKey];
            conditionalQs.forEach((condQ) => {
              const condAnswerValue = answers[condQ.id];
              if (
                condAnswerValue !== undefined &&
                condAnswerValue !== null &&
                condAnswerValue !== ""
              ) {
                let formattedCondAnswer = condAnswerValue;
                if (
                  condQ.type === "checkbox" &&
                  !Array.isArray(condAnswerValue)
                ) {
                  formattedCondAnswer = [condAnswerValue];
                }
                additionalAnswers.push({
                  question_id: condQ.id,
                  question_title: condQ.question_title || condQ.title,
                  answer: formattedCondAnswer,
                });
              }
            });
          }
        });
      }

      return additionalAnswers;
    };

    // Build payload with section and question answers
    for (const section of sections) {
      const sectionAnswers = [];
      if (section.questions && section.questions.length > 0) {
        for (const question of section.questions) {
          const questionId = question.id;
          const answerValue = answers[questionId];

          if (
            answerValue !== undefined &&
            answerValue !== null &&
            answerValue !== ""
          ) {
            let formattedAnswer = answerValue;
            if (question.type === "checkbox" && !Array.isArray(answerValue)) {
              formattedAnswer = [answerValue];
            } else if (Array.isArray(answerValue) && answerValue.length === 0) {
              continue;
            }

            sectionAnswers.push({
              question_id: questionId,
              question_title: question.title,
              answer: formattedAnswer,
            });

            // Include conditional question answers
            const conditionalAnswers = processConditionalQuestions(
              question,
              answerValue
            );
            sectionAnswers.push(...conditionalAnswers);
          }
        }
      }

      if (sectionAnswers.length > 0) {
        payload.sections.push({
          section_id: section.id,
          section_title: section.title,
          answers: sectionAnswers,
        });
      }
    }
    return payload;
  };

  // Save updated answers to the server
  const handleSaveAnswers = async () => {
    setSaving(true);
    try {
      const payload = prepareUpdatePayload(answers, sections, user?.id);

      const response = await updateOnboardingAnswers(user.id, payload);
      notify.success("Your onboarding answers have been updated successfully!");
      // Scroll to top after saving
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      console.error("Error updating answers:", error);
      notify.error(error.response?.data?.message || "Failed to update answers");
    } finally {
      setSaving(false);
    }
  };

  // Render conditional questions based on user answers
  const renderConditionalQuestions = (conditionalQuestions, options, id) => {
    if (!conditionalQuestions || typeof conditionalQuestions !== "object")
      return null;
    return Object.entries(conditionalQuestions).map(
      ([condKey, condQuestions]) => {
        // Extract option index from conditional key
        const lastDash = condKey.lastIndexOf("-");
        const optionIndex = parseInt(condKey.substring(lastDash + 1), 10);
        let optionValue = null;
        if (
          Array.isArray(options) &&
          !isNaN(optionIndex) &&
          options.length > optionIndex
        ) {
          optionValue = options[optionIndex];
        }
        const userAnswer = answers[id];
        // Determine if conditional questions should be shown
        let shouldShow = false;
        if (Array.isArray(userAnswer)) {
          shouldShow = optionValue && userAnswer.includes(optionValue);
        } else {
          shouldShow = userAnswer === optionValue;
        }
        if (shouldShow) {
          return condQuestions.map((cq) =>
            renderQuestion({
              ...cq,
              type: mapQuestionType(cq.question_type),
              options: extractOptionsFromAnswerFields(cq.answer_fields),
              conditionalQuestions: cq.conditional_questions,
              title: cq.question_title,
            })
          );
        }
        return null;
      }
    );
  };

  // Render individual questions based on type
  const renderQuestion = (question) => {
    const {
      id,
      title,
      subtitle,
      type,
      options = [],
      maxSelect,
      minSelect,
      placeholder,
      specialLogic,
      conditionalQuestions,
    } = question;

    switch (type) {
      case "radio":
        return (
          <div key={id} className="onboarding-question-block">
            <h4 className="onboarding-question-title">{title}</h4>
            {subtitle && (
              <p className="onboarding-question-subtitle">{subtitle}</p>
            )}
            <div className="onboarding-options-grid">
              {options.map((option, idx) => (
                <div
                  key={idx}
                  className={`onboarding-option-card ${
                    answers[id] === option ? "selected" : ""
                  }`}
                  onClick={() => handleRadioToggle(id, option)}
                >
                  <input
                    type="radio"
                    checked={answers[id] === option}
                    readOnly
                  />
                  <span className="onboarding-option-text">{option}</span>
                </div>
              ))}
            </div>
            {renderConditionalQuestions(conditionalQuestions, options, id)}
          </div>
        );

      case "checkbox":
        return (
          <div key={id} className="onboarding-question-block">
            <h4 className="onboarding-question-title">{title}</h4>
            {subtitle && (
              <p className="onboarding-question-subtitle">{subtitle}</p>
            )}
            {(maxSelect || minSelect) && (
              <div className="onboarding-multi-select-info">
                {minSelect && maxSelect
                  ? `Select between ${minSelect} and ${maxSelect} options`
                  : maxSelect
                  ? `Select up to ${maxSelect} options`
                  : minSelect
                  ? `Select at least ${minSelect} options`
                  : ""}
              </div>
            )}
            <div className="onboarding-options-grid">
              {options.map((option, idx) => (
                <div
                  key={idx}
                  className={`onboarding-option-card ${
                    (answers[id] || []).includes(option) ? "selected" : ""
                  }`}
                  onClick={() => {
                    if (specialLogic) {
                      handleSpecialCheckboxLogic(id, option, specialLogic);
                    } else {
                      handleCheckboxToggle(id, option, maxSelect, minSelect);
                    }
                  }}
                >
                  <input
                    type="checkbox"
                    checked={(answers[id] || []).includes(option)}
                    readOnly
                  />
                  <span className="onboarding-option-text">{option}</span>
                </div>
              ))}
            </div>
            {renderConditionalQuestions(conditionalQuestions, options, id)}
          </div>
        );

      case "email":
      case "date":
        return (
          <div key={id} className="onboarding-question-block">
            <h4 className="onboarding-question-title">{title}</h4>
            {subtitle && (
              <p className="onboarding-question-subtitle">{subtitle}</p>
            )}
            <input
              type={type}
              className="onboarding-input-field"
              placeholder={placeholder}
              value={answers[id] || ""}
              onChange={(e) => handleAnswerChange(id, e.target.value)}
            />
            {renderConditionalQuestions(conditionalQuestions, options, id)}
          </div>
        );

      case "textarea":
        return (
          <div key={id} className="onboarding-question-block">
            <h4 className="onboarding-question-title">{title}</h4>
            {subtitle && (
              <p className="onboarding-question-subtitle">{subtitle}</p>
            )}
            <textarea
              className="onboarding-input-field"
              placeholder={placeholder}
              rows={4}
              value={answers[id] || ""}
              onChange={(e) => handleAnswerChange(id, e.target.value)}
            />
            {renderConditionalQuestions(conditionalQuestions, options, id)}
          </div>
        );

      default:
        return null;
    }
  };

  // Render loading state
  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading your onboarding data...</p>
      </div>
    );
  }

  // Render message if onboarding not completed
  if (!onboardingCompleted) {
    return (
      <div className="onboarding-update-section">
        <div className="no-answers">
          <div className="no-answers-icon">📋</div>
          <h3>No Onboarding Answers Found</h3>
          <p>
            You haven't completed the onboarding questionnaire yet, but you can
            start filling it out here.
          </p>
        </div>
      </div>
    );
  } else {
    // Render main onboarding update form
    return (
      <div className="onboarding-update-section">
        <div className="onboarding-update-header">
          <h3>Update Onboarding Answers</h3>
          <p>Review and update your onboarding questionnaire responses</p>
        </div>

        <div className="onboarding-sections-container">
          {sections.map((section) => (
            <div key={section.id} className="onboarding-section-card">
              <div
                className="onboarding-section-header"
                onClick={() => toggleSection(section.id)}
              >
                <div className="onboarding-section-title-container">
                  <h4 className="onboarding-section-title">{section.title}</h4>
                  {section.subtitle && (
                    <p className="onboarding-section-subtitle">
                      {section.subtitle}
                    </p>
                  )}
                </div>
                <button className="onboarding-section-toggle">
                  {expandedSections[section.id] ? "▲" : "▼"}
                </button>
              </div>

              {expandedSections[section.id] && (
                <div className="onboarding-section-content">
                  {section.questions?.map((question) =>
                    renderQuestion(question)
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="onboarding-update-actions">
          <button
            className="btn-save-onboarding"
            onClick={handleSaveAnswers}
            disabled={saving}
          >
            {saving ? (
              <>
                <div className="loading-spinner-small"></div>
                Saving Changes...
              </>
            ) : (
              <>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 16 16"
                >
                  <path
                    fill="currentColor"
                    d="M8.5 1.5A1.5 1.5 0 0 1 10 0h4a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V2a2 2 0 0 1 2-2h6c-.314.418-.5.937-.5 1.5v7.793L4.854 6.646a.5.5 0 1 0-.708.708l3.5 3.5a.5.5 0 0 0 .708 0l3.5-3.5a.5.5 0 0 0-.708-.708L8.5 9.293z"
                  />
                </svg>
                Save Changes
              </>
            )}
          </button>
        </div>
      </div>
    );
  }
};

export default UpdateOnboardingSection;
